import React, { useState, useEffect } from 'react';
import { getUpcomingEventsForReminders, getEventAttendees, generateEventReminderEmail, generateEventReminderHtml, wasEventReminderSent, logEventReminder } from '../../lib/eventReminders';

const AdminEventRemindersPage = () => {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState({});
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchUpcomingEvents();
  }, []);

  const fetchUpcomingEvents = async () => {
    setLoading(true);
    try {
      const upcomingEvents = await getUpcomingEventsForReminders(14); // Next 14 days
      setEvents(upcomingEvents);
    } catch (error) {
      console.error('Error fetching events:', error);
      setMessage({ type: 'error', text: 'Failed to fetch upcoming events' });
    } finally {
      setLoading(false);
    }
  };

  const handleEventSelect = async (event) => {
    setSelectedEvent(event);
    if (event) {
      try {
        const eventAttendees = await getEventAttendees(event.id);
        setAttendees(eventAttendees);
      } catch (error) {
        console.error('Error fetching attendees:', error);
        setMessage({ type: 'error', text: 'Failed to fetch event attendees' });
      }
    }
  };

  const sendReminder = async (attendee) => {
    if (!selectedEvent) return;
    
    setSending(prev => ({ ...prev, [attendee.user_id]: true }));
    setMessage({ type: '', text: '' });

    try {
      // Check if reminder was sent recently
      const wasSent = await wasEventReminderSent(selectedEvent.id, attendee.profiles?.email, 7);
      
      if (wasSent) {
        setMessage({ type: 'warning', text: 'Reminder was already sent to this attendee in the last 7 days' });
        return;
      }

      // Generate email content
      const emailContent = generateEventReminderEmail(selectedEvent, attendee);
      const emailHtml = generateEventReminderHtml(selectedEvent, attendee);

      // Send via Netlify function
      const response = await fetch('/.netlify/functions/send-event-reminder', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          eventId: selectedEvent.id,
          recipientEmail: attendee.profiles?.email,
          attendeeName: attendee.profiles?.full_name,
          subject: emailContent.subject,
          body: emailContent.body,
          html: emailHtml
        }),
      });

      const result = await response.json();

      if (result.success) {
        setMessage({ type: 'success', text: `Reminder sent to ${attendee.profiles?.full_name}` });
      } else {
        setMessage({ type: 'error', text: result.error || 'Failed to send reminder' });
      }
    } catch (error) {
      console.error('Error sending reminder:', error);
      setMessage({ type: 'error', text: 'Failed to send reminder' });
    } finally {
      setSending(prev => ({ ...prev, [attendee.user_id]: false }));
    }
  };

  const sendAllReminders = async () => {
    if (!selectedEvent || attendees.length === 0) return;
    
    const confirm = window.confirm(`Send reminders to all ${attendees.length} attendees?`);
    if (!confirm) return;

    setSending({});
    setMessage({ type: '', text: 'Sending reminders to all attendees...' });

    for (const attendee of attendees) {
      try {
        const wasSent = await wasEventReminderSent(selectedEvent.id, attendee.profiles?.email, 7);
        if (!wasSent) {
          await sendReminder(attendee);
        }
      } catch (error) {
        console.error(`Failed to send reminder to ${attendee.profiles?.email}:`, error);
      }
    }

    setMessage({ type: 'success', text: 'Reminders sent to all eligible attendees' });
  };

  const getDaysUntil = (eventDate) => {
    const today = new Date();
    const event = new Date(eventDate);
    const days = Math.ceil((event - today) / (1000 * 60 * 60 * 24));
    return days;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Event Reminders</h1>
        <p className="mt-2 text-gray-600">Send reminder emails to registered attendees</p>
      </div>

      {message.text && (
        <div className={`mb-6 p-4 rounded-lg ${
          message.type === 'success' ? 'bg-green-50 text-green-800' :
          message.type === 'error' ? 'bg-red-50 text-red-800' :
          'bg-yellow-50 text-yellow-800'
        }`}>
          {message.text}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Events List */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Upcoming Events</h2>
          </div>
          <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-6 text-center text-gray-500">Loading events...</div>
            ) : events.length === 0 ? (
              <div className="p-6 text-center text-gray-500">No upcoming events in the next 14 days</div>
            ) : (
              events.map(event => (
                <div
                  key={event.id}
                  className={`p-4 hover:bg-gray-50 cursor-pointer ${
                    selectedEvent?.id === event.id ? 'bg-blue-50' : ''
                  }`}
                  onClick={() => handleEventSelect(event)}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-medium text-gray-900">{event.title}</h3>
                      <p className="text-sm text-gray-500">{event.location || event.venue_name}</p>
                      <p className="text-sm text-gray-500">
                        {new Date(event.start_date).toLocaleDateString()} - {getDaysUntil(event.start_date)} days
                      </p>
                    </div>
                    <span className="text-sm text-gray-400">
                      {event.event_registrations?.length || 0} registered
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Attendees List */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900">
              Attendees {selectedEvent && `(${attendees.length})`}
            </h2>
            {selectedEvent && attendees.length > 0 && (
              <button
                onClick={sendAllReminders}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark text-sm"
              >
                Send All Reminders
              </button>
            )}
          </div>
          <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
            {!selectedEvent ? (
              <div className="p-6 text-center text-gray-500">Select an event to view attendees</div>
            ) : attendees.length === 0 ? (
              <div className="p-6 text-center text-gray-500">No registered attendees</div>
            ) : (
              attendees.map(attendee => (
                <div key={attendee.user_id} className="p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium text-gray-900">
                        {attendee.profiles?.full_name || 'N/A'}
                      </p>
                      <p className="text-sm text-gray-500">
                        {attendee.profiles?.email}
                      </p>
                      <p className="text-xs text-gray-400">
                        Type: {attendee.registration_type || 'attendee'}
                      </p>
                    </div>
                    <button
                      onClick={() => sendReminder(attendee)}
                      disabled={sending[attendee.user_id]}
                      className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                    >
                      {sending[attendee.user_id] ? 'Sending...' : 'Send Reminder'}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminEventRemindersPage;
